import React, { useState, useEffect } from 'react';
import { Link, useLocation } from "react-router-dom";
import { ConnectWallet } from "@thirdweb-dev/react";
import { useTranslation } from 'react-i18next';
import "../styles/header.css";

const Header = () => {
  const [langDropdown, setLangDropdown] = useState(false);
  const [currentLang, setCurrentLang] = useState({ code: 'es', flag: '/flags/es.svg' });
  const location = useLocation();
  const { t, i18n } = useTranslation(); // Hook de traducción
  
  // Estado para controlar si el menú móvil está abierto
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Estado para detectar si estamos en móvil
  const [isMobile, setIsMobile] = useState(false);
  
  // Determina si un enlace está activo
  const isActive = (path) => location.pathname === path;
  
  // Obtener nombre de la página activa para mostrar en móvil
  const getActivePage = () => {
    if (isActive('/')) return t("nav.home");
    if (isActive('/about')) return t("nav.about");
    if (isActive('/tokenomics')) return t("nav.tokenomics");
    if (isActive('/roadmap')) return t("nav.roadmap");
    if (isActive('/staking')) return t("nav.staking");
    if (isActive('/vesting')) return t("nav.vesting");
    if (isActive('/dev-updates')) return t("nav.devupdates");
    return t("nav.home"); // Default
  };

  // Detectar tamaño de pantalla para activar modo móvil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      
      // Si pasamos a desktop, cerrar el menú móvil
      if (window.innerWidth > 768) {
        setMobileMenuOpen(false);
      }
    };
    
    // Comprobar al inicio
    checkMobile();
    
    // Escuchar cambios de tamaño
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Efecto para cerrar dropdown y menú al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Para dropdown de idioma - ahora comprueba tanto desktop como móvil
      if (!e.target.closest('.lang-selector') && !e.target.closest('.mobile-lang-selector')) {
        setLangDropdown(false);
      }
      
      // Para menú móvil (solo cerrar si se hace clic fuera y no en el botón de menú)
      if (isMobile && mobileMenuOpen && !e.target.closest('.mobile-menu') && 
          !e.target.closest('.menu-toggle')) {
        setMobileMenuOpen(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isMobile, mobileMenuOpen]);

  // Cargar preferencia de idioma guardada
  useEffect(() => {
    const savedLang = localStorage.getItem('selectedLanguage');
    const savedFlagSrc = localStorage.getItem('selectedFlagSrc');
    
    if (savedLang && savedFlagSrc) {
      setCurrentLang({ code: savedLang, flag: savedFlagSrc });
      i18n.changeLanguage(savedLang); // Cambiar el idioma en i18next
    }
  }, [i18n]);

  // Función para cambiar el idioma
  const changeLanguage = (code, flagSrc) => {
    console.log(`Cambiando idioma a: ${code}`);
    
    // Actualizar estado local
    setCurrentLang({ code: code, flag: flagSrc });
    
    // Guardar en localStorage
    localStorage.setItem('selectedLanguage', code);
    localStorage.setItem('selectedFlagSrc', flagSrc);
    
    // Cambiar idioma en i18next
    i18n.changeLanguage(code, (err, t) => {
      if (err) {
        console.error(`Error al cambiar idioma: ${err}`);
      } else {
        console.log(`Idioma cambiado exitosamente a: ${i18n.language}`);
        console.log(`Traducción de prueba - Home: ${t('nav.home')}`);
        
        // Forzar actualización del componente
        setCurrentLang(prev => ({...prev}));
      }
    });
    
    // Cerrar dropdown
    setLangDropdown(false);
  };

  // Ejemplo de mensajes para el ticker
  const tickerMessages = [
    t('ticker.message1'),
    t('ticker.message2'),
    t('ticker.message3')
  ];

  // Componente NewsTicker integrado directamente en Header
  const NewsTicker = () => {
    return (
      <div
        className="ticker-container"
        //onMouseEnter={() => setIsPaused(true)}
        //onMouseLeave={() => setIsPaused(false)}
      >
        <span className="ticker-label">{t("news.label")}:</span>
        <div 
          className="ticker-content"
          style={{ 
            //animationPlayState: isPaused ? 'paused' : 'running',
            transition: 'animationPlayState 0.3s ease-in-out',
            textTransform: 'uppercase'
          }}
        >
          {tickerMessages.map((message, index) => (
            <React.Fragment key={index}>
              <span>{message}</span>
              {index < tickerMessages.length - 1 && <span className="separator">|</span>}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  // Función para alternar el menú móvil
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Cerrar el menú móvil después de hacer clic en un enlace
  const handleMobileLinkClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <>
      <header className={`header ${isMobile ? 'mobile' : 'desktop'} ${location.pathname === '/' ? 'home-page' : ''}`}>
        {/* VERSIÓN ESCRITORIO - Sin cambios */}
        {!isMobile && (
          <>
            <div className="nav-section">
              <div className="nav-menu">
                <Link to="/" className={`home-link ${isActive('/') ? 'active' : ''}`}>{t("nav.home")}</Link>
                <Link to="/about" className={isActive('/about') ? 'active' : ''}>{t("nav.about")}</Link>
                <Link to="/tokenomics" className={isActive('/tokenomics') ? 'active' : ''}>{t("nav.tokenomics")}</Link>
                <Link to="/roadmap" className={isActive('/roadmap') ? 'active' : ''}>{t("nav.roadmap")}</Link>
                <Link to="/staking" className={isActive('/staking') ? 'active' : ''}>{t("nav.staking")}</Link>
                <Link to="/vesting" className={isActive('/vesting') ? 'active' : ''}>{t("nav.vesting")}</Link>
                <Link to="/dev-updates" className={isActive('/dev-updates') ? 'active' : ''}>{t("nav.devupdates")}</Link>
              </div>
              
              <div className="right-elements">
                <div className="social-menu">
                  <a href="https://x.com/edop_token" target="_blank" rel="noopener noreferrer">
                    <img src="/social/x-gray.png" alt="Twitter" className="icon-gray" />
                    <img src="/social/x-color.png" alt="Twitter" className="icon-color" />
                  </a>
                  <a href="https://t.me/eDOPToken_esp" target="_blank" rel="noopener noreferrer">
                    <img src="/social/telegram-gray.png" alt="Telegram" className="icon-gray" />
                    <img src="/social/telegram-color.png" alt="Telegram" className="icon-color" />
                  </a>                  <a href="https://coinmarketcap.com/community/profile/edoptoken/" target="_blank" rel="noopener noreferrer">
                    <img src="/social/cmc-gray.png" alt="CoinMarketCap" className="icon-gray" />
                    <img src="/social/cmc-color.png" alt="CoinMarketCap" className="icon-color" />
                  </a>
                  <a href="https://www.dextools.io/app/es/bnb/pair-explorer/0xB8f68E8E6568e3b8529af5BF913115C8e50AffF7" target="_blank" rel="noopener noreferrer">
                    <img src="/social/dextool-gray.png" alt="DexTool" className="icon-gray" />
                    <img src="/social/dextool-color.png" alt="DexTool" className="icon-color" />
                  </a>
                  <a href="https://youtube.com/@edoptoken" target="_blank" rel="noopener noreferrer">
                    <img src="/social/youtube-gray.png" alt="YouTube" className="icon-gray" />
                    <img src="/social/youtube-color.png" alt="YouTube" className="icon-color" />
                  </a>
                </div>
                
                <ConnectWallet 
                  theme="light"
                  btnTitle={t("wallet.connect")}
                  className="connect-btn"
                  dropdownPosition={{
                    side: "bottom",
                    align: "center"
                  }}
                />
                
                <div 
                  className="lang-selector" 
                  onClick={(e) => {
                    if (e.target.closest('.lang-current') || e.target.closest('.lang-option')) {
                      e.stopPropagation();
                      setLangDropdown(!langDropdown);
                    }
                  }}
                >
                  <div className="lang-current">
                    <img src={currentLang.flag} alt={currentLang.code.toUpperCase()} className="lang-flag" /> 
                    {currentLang.code.toUpperCase()}
                  </div>
                  
                  {langDropdown && (
                    <div className="lang-dropdown show">
                      <div className="lang-option" onClick={() => changeLanguage('es', '/flags/es.svg')}>
                        <img src="/flags/es.svg" alt="Español" className="lang-flag" /> Español
                      </div>
                      <div className="lang-option" onClick={() => changeLanguage('en', '/flags/en.svg')}>
                        <img src="/flags/en.svg" alt="English" className="lang-flag" /> English
                      </div>
                      <div className="lang-option" onClick={() => changeLanguage('fr', '/flags/fr.svg')}>
                        <img src="/flags/fr.svg" alt="Français" className="lang-flag" /> Français
                      </div>
                      <div className="lang-option" onClick={() => changeLanguage('de', '/flags/de.svg')}>
                        <img src="/flags/de.svg" alt="Deutsch" className="lang-flag" /> Deutsch
                      </div>
                      <div className="lang-option" onClick={() => changeLanguage('it', '/flags/it.svg')}>
                        <img src="/flags/it.svg" alt="Italiano" className="lang-flag" /> Italiano
                      </div>
                      <div className="lang-option" onClick={() => changeLanguage('pt', '/flags/pt.svg')}>
                        <img src="/flags/pt.svg" alt="Português" className="lang-flag" /> Português
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Ocultar NewsTicker solo en la ruta Home */}
            {location.pathname !== '/' && <NewsTicker />}
          </>
        )}
        
        {/* VERSIÓN MÓVIL - Nueva implementación */}
        {isMobile && (
          <>
            <div className="mobile-header">
              <button 
                className={`menu-toggle ${mobileMenuOpen ? 'active' : ''}`} 
                onClick={toggleMobileMenu}
                aria-label="Menú"
              >
                <span></span>
                <span></span>
                <span></span>
              </button>
              
              <div className="mobile-current-page">
                {getActivePage()}
              </div>
            </div>
            
            {/* Menú móvil desplegable */}
            <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
              {/* Nav links en vertical */}
              <nav className="mobile-nav">
                <Link to="/" className={isActive('/') ? 'active' : ''} onClick={handleMobileLinkClick}>{t("nav.home")}</Link>
                <Link to="/about" className={isActive('/about') ? 'active' : ''} onClick={handleMobileLinkClick}>{t("nav.about")}</Link>
                <Link to="/tokenomics" className={isActive('/tokenomics') ? 'active' : ''} onClick={handleMobileLinkClick}>{t("nav.tokenomics")}</Link>
                <Link to="/roadmap" className={isActive('/roadmap') ? 'active' : ''} onClick={handleMobileLinkClick}>{t("nav.roadmap")}</Link>
                <Link to="/staking" className={isActive('/staking') ? 'active' : ''} onClick={handleMobileLinkClick}>{t("nav.staking")}</Link>
                <Link to="/vesting" className={isActive('/vesting') ? 'active' : ''} onClick={handleMobileLinkClick}>{t("nav.vesting")}</Link>
                <Link to="/dev-updates" className={isActive('/dev-updates') ? 'active' : ''} onClick={handleMobileLinkClick}>{t("nav.devupdates")}</Link>
              </nav>
              
              {/* Redes sociales en horizontal */}
              <div className="mobile-social">
                <a href="https://twitter.com/edop" target="_blank" rel="noopener noreferrer">
                  <img src="/social/x-color.png" alt="Twitter" />
                </a>
                <a href="https://t.me/edop" target="_blank" rel="noopener noreferrer">
                  <img src="/social/telegram-color.png" alt="Telegram" />
                </a>                <a href="https://coinmarketcap.com/community/profile/edoptoken/" target="_blank" rel="noopener noreferrer">
                  <img src="/social/cmc-color.png" alt="CoinMarketCap" />
                </a>
                <a href="https://www.dextools.io/app/es/bnb/pair-explorer/0x0000" target="_blank" rel="noopener noreferrer">
                  <img src="/social/dextool-color.png" alt="DexTool" />
                </a>
                <a href="https://youtube.com/@edoptoken" target="_blank" rel="noopener noreferrer">
                  <img src="/social/youtube-color.png" alt="YouTube" />
                </a>
              </div>
              
              {/* Selector de idioma */}
              <div className="mobile-lang-selector">
                <div className="mobile-lang-header" onClick={(e) => {
                  e.stopPropagation(); // Detener propagación del evento
                  setLangDropdown(!langDropdown);
                }}>
                  <img src={currentLang.flag} alt={currentLang.code.toUpperCase()} className="lang-flag" />
                  <span>{currentLang.code === 'es' ? 'Español' : 
                         currentLang.code === 'en' ? 'English' :
                         currentLang.code === 'fr' ? 'Français' :
                         currentLang.code === 'de' ? 'Deutsch' :
                         currentLang.code === 'it' ? 'Italiano' : 'Português'}</span>
                  <div className={`arrow-icon ${langDropdown ? 'up' : 'down'}`}></div>
                </div>
                
                {langDropdown && (
                  <div className="mobile-lang-options" onClick={(e) => e.stopPropagation()}>
                    <div onClick={() => changeLanguage('es', '/flags/es.svg')}>
                      <img src="/flags/es.svg" alt="Español" className="lang-flag" /> Español
                    </div>
                    <div onClick={() => changeLanguage('en', '/flags/en.svg')}>
                      <img src="/flags/en.svg" alt="English" className="lang-flag" /> English
                    </div>
                    <div onClick={() => changeLanguage('fr', '/flags/fr.svg')}>
                      <img src="/flags/fr.svg" alt="Français" className="lang-flag" /> Français
                    </div>
                    <div onClick={() => changeLanguage('de', '/flags/de.svg')}>
                      <img src="/flags/de.svg" alt="Deutsch" className="lang-flag" /> Deutsch
                    </div>
                    <div onClick={() => changeLanguage('it', '/flags/it.svg')}>
                      <img src="/flags/it.svg" alt="Italiano" className="lang-flag" /> Italiano
                    </div>
                    <div onClick={() => changeLanguage('pt', '/flags/pt.svg')}>
                      <img src="/flags/pt.svg" alt="Português" className="lang-flag" /> Português
                    </div>
                  </div>
                )}
              </div>
              
              {/* Botón de conectar wallet */}
              <div className="mobile-wallet">
                <ConnectWallet 
                  theme="light"
                  btnTitle={t("wallet.connect")}
                  className="mobile-connect-btn"
                  dropdownPosition={{
                    side: "top",
                    align: "center"
                  }}
                />
              </div>
            </div>
          </>
        )}
      </header>
    </>
  );
};

export default Header;
